import { useEffect, useMemo, useState } from "react";
import { FaBox } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  areContainerIdListsEqual,
  filterContainersForSite,
  formatContainerMonitoringLabel,
  formatContainersPickerSubtitle,
  isContainerSelected,
  normalizeContainerObjectIdList,
  toggleContainerSelection,
} from "./installationContainerUtils";
import { getContainersEditList } from "./installationApi";
import {
  InstallationPickerCheckboxOption,
  InstallationPickerRow,
  InstallationPickerSheet,
} from "./InstallationPickerSheet";

const InstallationSideContainersSection = ({
  api,
  token,
  corporationId,
  addressId,
  selectedContainerIds,
  onContainersSave,
  divided,
  disabled,
  saving,
}) => {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [containersLoading, setContainersLoading] = useState(false);
  const [siteContainers, setSiteContainers] = useState([]);

  const savedIds = useMemo(
    () => normalizeContainerObjectIdList(selectedContainerIds),
    [selectedContainerIds],
  );
  const [draftIds, setDraftIds] = useState(savedIds);

  useEffect(() => {
    setDraftIds(savedIds);
  }, [savedIds]);

  useEffect(() => {
    if (sheetOpen) {
      setDraftIds(savedIds);
    }
  }, [sheetOpen, savedIds]);

  useEffect(() => {
    if (!api || !token || !corporationId) {
      setSiteContainers([]);
      return;
    }

    let cancelled = false;
    setContainersLoading(true);

    void getContainersEditList(api, token, corporationId)
      .then((containersList) => {
        if (cancelled) {
          return;
        }
        setSiteContainers(filterContainersForSite(containersList, addressId));
      })
      .catch(() => {
        if (!cancelled) {
          setSiteContainers([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setContainersLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, token, corporationId, addressId]);

  const rowSubtitle = formatContainersPickerSubtitle(t, {
    addressId,
    loading: containersLoading,
    selectedCount: savedIds.length,
    totalCount: siteContainers.length,
  });

  const isDirty = !areContainerIdListsEqual(draftIds, savedIds);

  const closeSheet = () => {
    setDraftIds(savedIds);
    setSheetOpen(false);
  };

  const handleSave = async () => {
    const saved = await onContainersSave(draftIds);
    if (saved) {
      setSheetOpen(false);
    }
  };

  return (
    <>
      <InstallationPickerRow
        icon={FaBox}
        iconVariant="containers"
        title={t("installation_side_containers_title")}
        subtitle={rowSubtitle}
        onClick={() => setSheetOpen(true)}
        disabled={disabled || saving}
        divided={divided}
      />

      <InstallationPickerSheet
        open={sheetOpen}
        title={t("installation_picker_title_containers")}
        dirty={isDirty}
        saving={saving}
        onDismiss={closeSheet}
        onSave={() => void handleSave()}
      >
        {!addressId && (
          <p className="installation-picker-empty">
            {t("installation_side_containers_no_site")}
          </p>
        )}
        {addressId && containersLoading && (
          <p className="installation-picker-empty">
            {t("installation_loading")}
          </p>
        )}
        {addressId && !containersLoading && siteContainers.length === 0 && (
          <p className="installation-picker-empty">
            {t("installation_side_containers_empty")}
          </p>
        )}
        {addressId && !containersLoading && siteContainers.length > 0 && (
          <div className="installation-picker-checkbox-list">
            {siteContainers.map((container) => (
              <InstallationPickerCheckboxOption
                key={container._id}
                label={formatContainerMonitoringLabel(container)}
                selected={isContainerSelected(draftIds, container._id)}
                onToggle={() =>
                  setDraftIds((current) =>
                    toggleContainerSelection(current, container._id),
                  )
                }
              />
            ))}
          </div>
        )}
      </InstallationPickerSheet>
    </>
  );
};

export default InstallationSideContainersSection;
